import { locationSlug } from '@/helpers/urlTs';
import { DEFAULT_MY_LOCATION_KEYWORD } from '@/hooks/search/useSearch.constants';
import { SearchType } from '@/hooks/search/useSearch.helpers';
import { getCookie, isServer, server } from '../helpers';

export const autocompleteService = {
  getSuggestions,
  getLocationSuggestions,
  deleteLocationSuggestion,
};

function getSuggestions(query: string, searchType?: SearchType, signal?: AbortSignal) {
  query = (query || '').split('%25').join('%').split('%').join('%25');

  const base = '/search/suggestions';
  let searchQuery = `?q=${encodeURIComponent(query)}`;

  if (searchType === 'deals_preview' || searchType === 'deals') {
    searchQuery += '&deals=1';
  }

  if (searchType === 'sistaminuten' || searchType === 'deals_sistaminuten' || searchType === 'deals_preview') {
    searchQuery += '&sistaminuten=1';
  }

  const url = base + searchQuery;

  return server.request.get(url, { signal }).then(server.handleSuccess).catch(server.handleError);
}

const inflightLocationSuggestions = new Map<string, Promise<unknown>>();
const cachedLocationSuggestions = new Map<string, unknown>();

export function invalidateLocationSuggestionsCache() {
  inflightLocationSuggestions.clear();
  cachedLocationSuggestions.clear();
}

function getLocationSuggestions(query: string, signal?: AbortSignal, opts?: { authHeader?: string; locale?: string }) {
  query = query.split('%25').join('%').split('%').join('%25');
  const locationData = getCookie('BokadirektLocationData');
  let requestUrl = `/location/search?q=${query}`;
  let cacheKey = '';

  if (locationData?.location && locationSlug(locationData.location) === DEFAULT_MY_LOCATION_KEYWORD) {
    const [lat, lon] = locationData.position.split('-');
    if (lat && lon) {
      requestUrl += `&lat=${lat}&lon=${lon}`;
      cacheKey = `${lat}|${lon}`;
    }
  }

  const headers: Record<string, string> = {};
  if (opts?.authHeader) headers.MpAuthorization = opts.authHeader;
  if (opts?.locale) headers['Accept-Language'] = opts.locale;

  /**
   * cache only apply on client, isServer to prevent SSR cross-request leaks
   */
  const isCacheable = !isServer && query === '';

  if (isCacheable) {
    if (cachedLocationSuggestions.has(cacheKey)) {
      return Promise.resolve(cachedLocationSuggestions.get(cacheKey));
    }

    const inflight = inflightLocationSuggestions.get(cacheKey);
    if (inflight) return inflight;

    const promise = server.request
      .get(requestUrl, {})
      .then(server.handleSuccess)
      .then((value: unknown) => {
        cachedLocationSuggestions.set(cacheKey, value);
        return value;
      })
      .catch(server.handleError)
      .finally(() => {
        inflightLocationSuggestions.delete(cacheKey);
      });
    inflightLocationSuggestions.set(cacheKey, promise);
    return promise;
  }

  const requestConfig = Object.keys(headers).length ? { signal, headers } : { signal };
  return server.request.get(requestUrl, requestConfig).then(server.handleSuccess).catch(server.handleError);
}

function deleteLocationSuggestion(location: string) {
  const requestPayload = {
    location: location,
  };
  return server.request
    .post('/location/remove-from-history', requestPayload)
    .then(server.handleSuccess)
    .then((value: unknown) => {
      invalidateLocationSuggestionsCache();
      return value;
    })
    .catch(server.handleError);
}
